import { CommonModule } from '@angular/common';
import { Component, input, Input } from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { provideNativeDateAdapter } from '@angular/material/core';
import {
  MatCalendarCellClassFunction,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import {
  MatFormFieldAppearance,
  MatFormFieldModule,
} from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ErrorFieldComponent } from '../error-field/error-field.component';

@Component({
  selector: 'siat-datepicker-field',
  templateUrl: './datepicker-field.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ErrorFieldComponent,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
  ],
  providers: [provideNativeDateAdapter()],
})
export class DatepickerFieldComponent implements ControlValueAccessor {
  /**
   * The id attribute of the input field.
   */
  @Input()
  id: string;

  /**
   * The name attribute of the input field.
   */
  @Input()
  name: string;

  /**
   * The control name associated with the input field.
   */
  @Input()
  controlName: string;

  /**
   * The form group associated with the input field.
   */
  @Input()
  formGroup: FormGroup;

  /**
   * The input placeholder.
   */
  @Input()
  placeholder: string;

  /**
   * The input label.
   */
  @Input()
  label: string;

  /**
   * Placement of the datepicker popup.
   */
  @Input()
  placement: 'auto' | 'top' | 'bottom' | 'left' | 'right' = 'auto';

  /**
   * The style class of the field.
   */
  @Input()
  fieldClass: string;

  /**
   * The appearance of the form field.
   */
  @Input()
  appareance: MatFormFieldAppearance = 'outline';

  /**
   * The minimum selectable date.
   */
  minDate = input<Date | null>(null);

  /**
   * The maximum selectable date.
   */
  @Input()
  maxDate?: Date;

  /** get date to apply particular css property */
  @Input() dateListCss?: number[];


  /**
   * The date filter function.
   */
  @Input()
  dateFilter?: (date: Date | null) => boolean;

  /**
   * Get control.
   */
  get control(): FormControl<Date> {
    return this.formGroup?.controls[this.controlName] as FormControl<Date>;
  }

  /**
   * Keeps a reference to the function to be called every time
   * the value of the field is changed in the UI.
   */
  onChange: (value: Date) => void = () => { };

  /**
   * Keeps a reference to the function to be called every time
   * the field has to be considered touched (normally on blur).
   */
  onTouched: () => void = () => { };

  /**
   * Registers a function to call when the field value changes in the UI.
   * This is needed to communicate the update to angular forms API.
   */
  registerOnChange(changeCallback: (value: Date) => void): void {
    this.onChange = changeCallback;
  }

  /**
   * Allows to register a function to be called when the field is touched
   * (it's a "noop" method but it's needed to correctly implement ControlValueAccessor interface).
   */
  registerOnTouched(touchedCallback: () => void): void {
    this.onTouched = touchedCallback;
  }

  /**
   * @inheritDoc
   */
  writeValue(value: Date): void {
    // Prevent the propagation to the view if the provided value is not a Date
    if (!value) return;
    this.control.setValue(value);
  }

  dateClass: MatCalendarCellClassFunction<Date> = cellDate => {
    const date = cellDate.getDate();
    if (this.dateListCss?.includes(date)) {
      return 'highlight-date';
    } else {
      return '';
    }
  };
}
